import { Fragment, type ReactNode } from "react";
import { Text } from "react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import type {
  PlanAction,
  PlanStep,
  StartTreatmentPresetCycleType,
} from "../plan.types";
import { useManualStepCompletion } from "../hooks/useManualStepCompletion";
import {
  PreCycleHeroCard,
  type PreCycleHeroSnapshotItem,
  type PreCyclePrepStep,
} from "./PreCycleHeroCard";
import type { HeroCycleEnding } from "./hero/Hero";
import { Stepper } from "./Stepper";

/**
 * Actions that change the patient's own plan or cycle. A partner reading the
 * plan is not offered them.
 */
function isPatientOnlyAction(action: PlanAction | undefined): boolean {
  return (
    action?.signal === "open-start-treatment-modal" ||
    action?.signal === "open-intent-picker" ||
    action?.signal === "open-partner-or-donor-modal" ||
    action?.signal === "close-recorded-cycle"
  );
}

export type PreCycleHomeSection = {
  id: string;
  render: () => ReactNode;
  includeInPlanSetup?: boolean;
  progressStepIds?: string[];
  /**
   * StepperSection id this section's "Mark as done" persists under
   * (homepage_state.taskSections.completed). When set, it drives the hero
   * "N of M steps complete" badge; otherwise the legacy progressStepIds/status
   * path is used.
   */
  completionStepId?: string;
  /**
   * A step only the patient can take, such as starting the cycle. A partner
   * reads the same plan without it, rather than with a button that would open
   * a cycle on the wrong record.
   */
  patientOnly?: boolean;
};

type Props = {
  activeStep: PlanStep | null;
  nextAction?: PlanAction;
  steps: PreCyclePrepStep[];
  completedSteps: number;
  totalSteps: number;
  eyebrow: string;
  title: string;
  snapshotItems: PreCycleHeroSnapshotItem[];
  phaseDescription: string;
  intro: string;
  sections: PreCycleHomeSection[];
  onAction: (action: PlanAction) => void;
  onOpenPlan: () => void;
  onEditPlan: () => void;
  /**
   * The viewer is a partner reading someone else's plan. The plan, its steps
   * and its progress stay; starting the cycle, adding its dates and editing
   * the plan are the patient's, so they are not offered.
   */
  viewerIsSupporting?: boolean;
  showStartCycleAction?: boolean;
  startCyclePreset?: StartTreatmentPresetCycleType;
  openPlanAccessibilityLabel?: string;
  planAccessibilityLabel?: string;
  /** A cycle that has just ended, announced by the hero rather than above it. */
  cycleEnding?: HeroCycleEnding | null;
};

export function PreCycleHome({
  activeStep,
  nextAction,
  steps,
  eyebrow,
  title,
  snapshotItems,
  phaseDescription,
  intro,
  sections: allSections,
  onAction,
  onOpenPlan,
  onEditPlan,
  viewerIsSupporting = false,
  showStartCycleAction,
  startCyclePreset,
  openPlanAccessibilityLabel,
  planAccessibilityLabel,
  cycleEnding,
}: Props) {
  const { completed: completedPlanStepIds } = useManualStepCompletion();
  const sections = viewerIsSupporting
    ? allSections.filter((section) => !section.patientOnly)
    : allSections;
  const heroSnapshotItems = viewerIsSupporting
    ? snapshotItems.map(({ action, ...item }) =>
        isPatientOnlyAction(action) ? item : { ...item, action },
      )
    : snapshotItems;
  const heroActiveStep =
    viewerIsSupporting && activeStep
      ? {
          ...activeStep,
          actions: activeStep.actions.filter(
            (action) => !isPatientOnlyAction(action),
          ),
        }
      : activeStep;
  const heroNextAction =
    viewerIsSupporting && isPatientOnlyAction(nextAction)
      ? undefined
      : nextAction;
  const planSetupSections = sections.filter(
    (section) => section.includeInPlanSetup !== false,
  );
  const planSetupTotalSteps = planSetupSections.length;
  const statusByStepId = new Map(
    steps.map((step) => [step.id, step.status] as const),
  );
  const planSetupCompletedSteps = planSetupSections.filter((section) => {
    // Preferred: the step card and plan action share this journey-scoped
    // completion ID, so the badge tracks the same persisted progress.
    if (section.completionStepId) {
      return completedPlanStepIds.includes(section.completionStepId);
    }
    const progressStepIds = section.progressStepIds ?? [section.id];
    if (progressStepIds.length === 0) return false;
    return progressStepIds.every(
      (stepId) => statusByStepId.get(stepId) === "complete",
    );
  }).length;

  return (
    <>
      <PreCycleHeroCard
        activeStep={heroActiveStep}
        nextAction={heroNextAction}
        completedSteps={planSetupCompletedSteps}
        totalSteps={planSetupTotalSteps}
        eyebrow={eyebrow}
        title={title}
        snapshotItems={heroSnapshotItems}
        phaseDescription={phaseDescription}
        openPlanAccessibilityLabel={openPlanAccessibilityLabel}
        planAccessibilityLabel={planAccessibilityLabel}
        onAction={onAction}
        onOpenPlan={onOpenPlan}
        onEditPlan={viewerIsSupporting ? undefined : onEditPlan}
        showStartCycleAction={showStartCycleAction && !viewerIsSupporting}
        startCyclePreset={startCyclePreset}
        cycleEnding={cycleEnding}
      />

      <Text
        className="font-dm-sans"
        style={{
          marginTop: 34,
          marginBottom: 18,
          paddingHorizontal: 4,
          fontSize: 18,
          lineHeight: 24,
          color: EMBER.colors.textLight,
          fontWeight: "700",
        }}
      >
        {intro}
      </Text>

      <Stepper>
        {sections.map((section) => (
          <Fragment key={section.id}>{section.render()}</Fragment>
        ))}
      </Stepper>
    </>
  );
}
